import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Button } from './components/ui/button';
import { X, Download } from 'lucide-react';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

const OfficeViewerModal = ({ isOpen, onClose, fileUrl, fileName, onDownload }) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Detectar tipo de arquivo
  const isWord = fileName?.toLowerCase().includes('.doc');
  const isExcel = fileName?.toLowerCase().includes('.xls');
  const isPowerPoint = fileName?.toLowerCase().includes('.ppt');
  
  const fileIcon = isWord ? '📝' : isExcel ? '📊' : isPowerPoint ? '📑' : '📄';

  useEffect(() => {
    if (!isOpen || !fileUrl) return;

    const loadDocument = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('📄 Carregando documento Office...');
        
        // Buscar arquivo
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        if (isWord) {
          // Renderizar Word com Mammoth.js - configuração avançada
          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              styleMap: [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
                "p[style-name='Title'] => h1.title:fresh",
                "p[style-name='Subtitle'] => h2.subtitle:fresh",
                "r[style-name='Strong'] => strong",
                "r[style-name='Emphasis'] => em"
              ],
              includeDefaultStyleMap: true,
              convertImage: mammoth.images.imgElement((image) => {
                return image.read("base64").then((imageBuffer) => {
                  return {
                    src: `data:${image.contentType};base64,${imageBuffer}`
                  };
                });
              })
            }
          );
          
          // Adicionar wrapper com estilos de documento Word
          const styledContent = `
            <div class="word-document">
              ${result.value}
            </div>
          `;
          
          setContent(styledContent);
          console.log('✅ Documento Word renderizado com formatação');
          
          if (result.messages.length > 0) {
            console.log('ℹ️ Avisos de conversão:', result.messages);
          }
        } else if (isExcel) {
          // Renderizar Excel com SheetJS - todas as planilhas
          const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });
          
          let htmlContent = '';
          
          // Renderizar todas as abas
          workbook.SheetNames.forEach((sheetName, index) => {
            const worksheet = workbook.Sheets[sheetName];
            const html = XLSX.utils.sheet_to_html(worksheet, { 
              header: '', 
              id: `excel-sheet-${index}`,
              editable: false
            });
            
            htmlContent += `
              <div class="excel-sheet">
                <div class="excel-sheet-header">
                  <h3>📊 ${sheetName}</h3>
                </div>
                <div class="excel-sheet-content">
                  ${html}
                </div>
              </div>
            `;
          });
          
          setContent(htmlContent);
          console.log(`✅ Planilha Excel renderizada com ${workbook.SheetNames.length} aba(s)`);
        } else {
          setError('Tipo de arquivo não suportado para visualização. Use Download.');
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('❌ Erro ao carregar documento:', err);
        setError(err.message || 'Erro ao carregar documento');
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [isOpen, fileUrl, isWord, isExcel]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent 
        className="max-w-6xl w-full h-[95vh] p-0 flex flex-col overflow-hidden"
        style={{ zIndex: 99999 }}
      >
        <DialogHeader 
          className="p-4 border-b flex flex-row items-center justify-between flex-shrink-0 bg-white" 
          style={{ position: 'sticky', top: 0, zIndex: 10 }}
        >
          <DialogTitle className="text-lg font-semibold truncate flex-1">
            {fileIcon} {fileName || 'Documento Office'}
          </DialogTitle>
          <div className="flex items-center gap-2">
            {/* Download */}
            {onDownload && (
              <Button
                variant="outline"
                size="sm"
                onClick={onDownload}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            )}
            
            {/* Fechar */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto bg-gray-50 p-6" style={{ maxHeight: 'calc(95vh - 80px)' }}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Carregando {isWord ? 'documento' : isExcel ? 'planilha' : 'arquivo'}...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
                <p className="text-red-600 font-semibold mb-2">❌ {error}</p>
                <Button onClick={onDownload} className="mt-4">
                  📥 Baixar Arquivo
                </Button>
              </div>
            </div>
          ) : (
            <div className={isExcel ? '' : 'flex justify-center'}>
              <div 
                className={`${isExcel ? 'w-full' : 'word-document'} office-viewer-content`}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfficeViewerModal;
